import React, { useState } from 'react';
import { Table } from 'reactstrap';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import BoldButton from '../Common/BoldButton';
import { textSatisfiesFilter } from '../FilterInput';
import QueryView from '../Common/QueryView';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import useGetPurchaseOrders, { PurchaseOrderDto } from '../Requests/PurchaseOrders/useGetPurchaseOrders';
import { CreateEditPurchaseOrder } from './CreateEditPurchaseOrder';
import useRemovePurchaseOrder from '../Requests/PurchaseOrders/useRemovePurchaseOrder';
import Confirm from '../Common/Confirm';
import useCompletePurchaseOrder from '../Requests/PurchaseOrders/useCompletePurchaseOrder';
import { toAbbreviation } from '../Common/Units';

export type PoModalState =
  { state: 'Hidden' } |
  { state: 'Create' } |
  { state: 'Edit', po: PurchaseOrderDto } |
  { state: 'View', po: PurchaseOrderDto };

type PurchaseOrdersTabProps = {
  filterText: string;
  completed: boolean;
  modalState: PoModalState,
  setModalState: (modalState: PoModalState) => void;
  setViewTransaction: (po: PurchaseOrderDto) => void;
  requestSuccessful: (message: string) => void;
  requestFailed: (err: string) => void;
}

const PurchaseOrdersTab = (props: PurchaseOrdersTabProps) => {
  const getPOs = useGetPurchaseOrders({ completed: props.completed });

  const requestSuccessful = (message: string) => {
    props.requestSuccessful(message);
    getPOs.query();
  };

  return <QueryView query={getPOs}
    renderLoading={() =>
      <div className="mt-8">
        <CenteredSpinner />
      </div>}
    renderData={purchaseOrders =>
      <PurchaseOrders
        {...props}
        purchaseOrders={purchaseOrders}
        requestSuccessful={requestSuccessful}
        requestFailed={props.requestFailed} />
    } />;
}

const PurchaseOrders = (props: PurchaseOrdersTabProps & { purchaseOrders: PurchaseOrderDto[] }) => {
  const [poToRemove, setPoToRemove] = useState<PurchaseOrderDto | undefined>();
  const [poToComplete, setPoToComplete] = useState<PurchaseOrderDto | undefined>();

  const removePo = useRemovePurchaseOrder(poToRemove?.id, {
    onSuccess: () => props.requestSuccessful(`Purchase order ${poToRemove?.number} removed`),
    onError: err => props.requestFailed(err.message),
    onComplete: () => setPoToRemove(undefined),
  });

  const completePo = useCompletePurchaseOrder(poToComplete?.id, {
    onSuccess: () => props.requestSuccessful(`Purchase order ${poToComplete?.number} completed`),
    onError: err => props.requestFailed(err.message),
    onComplete: () => setPoToComplete(undefined),
  });

  const containsFilterText = (po: PurchaseOrderDto) => {
    const satisfiesFilter = textSatisfiesFilter(props.filterText);
    return satisfiesFilter(po.number) || satisfiesFilter(po.supplierName) || satisfiesFilter(po.productName);
  };

  const sortAndFilterSuppliers = (purchaseOrders: PurchaseOrderDto[]) =>
    purchaseOrders.sort((a, b) => a.number.localeCompare(b.number)).filter(containsFilterText);

  const numberClicked = (po: PurchaseOrderDto) => {
    if (po.hasTransactions) {
      console.log("here");
      props.setModalState({ state: 'View', po });
    } else {
      props.setModalState({ state: 'Edit', po });
    }
  }

  return <Table bordered>
    {
      props.modalState.state !== 'Hidden' &&
      <CreateEditPurchaseOrder
        initPo={(props.modalState.state === 'Edit' || props.modalState.state === 'View') ? props.modalState.po : undefined}
        viewOnly={props.modalState.state === 'View'}
        onHide={() => props.setModalState({ state: 'Hidden' })}
        onSuccess={po => {
          props.setModalState({ state: 'Hidden' })
          props.requestSuccessful(`Purchase Order ${po.number} ${props.modalState.state === 'Edit' ? 'updated' : 'created'}`);
        }}
      />
    }
    {
      poToRemove &&
      <Confirm
        visible={!!poToRemove}
        title='Remove purchase order'
        body={`When a purchase order is removed it cannot be restored. 
             Are you sure you want to remove ${poToRemove?.number}?`}
        isLoading={removePo.isLoading}
        onDismiss={() => setPoToRemove(undefined)}
        onConfirm={() => removePo.request()} />
    }
    {
      poToComplete &&
      <Confirm
        visible={!!poToComplete}
        title='Complete purchase order'
        body={`When a purchase order is completed it is no longer available to be received. 
               Completion cannot be undone. Are you sure that you want to complete
               purchase order ${poToComplete?.number}?`}
        isLoading={completePo.isLoading}
        onDismiss={() => setPoToComplete(undefined)}
        onConfirm={() => completePo.request()} />
    }
    <thead>
      <tr>
        <th className="col-1">PO Number</th>
        <th className="col-2">Supplier</th>
        <th className="col-2">Product</th>
        <th className="col-1">Purchased</th>
        <th className="col-1">Received</th>
        <th className="col-1">Site</th>
        <th className="col-1" />
      </tr>
    </thead>
    <tbody>
      {sortAndFilterSuppliers(props.purchaseOrders).map(po =>
        <tr key={po.id}>
          <td>
            <Button
              variant="link"
              onClick={() => numberClicked(po)}>
              {po.number}
            </Button>
          </td>
          <td>{po.supplierName}</td>
          <td>{po.productName}</td>
          <td>{po.purchased} {toAbbreviation(po.unit)}</td>
          <td>{
            po.received !== undefined
              ? po.received.toFixed(2) + " " + toAbbreviation(po.unit)
              : <OverlayTrigger
                key={po.id}
                delay={{ show: 250, hide: 400 }}
                overlay={
                  <Tooltip>
                    {`Could not convert received amount to ${po.unit}`}
                  </Tooltip>
                }><p className="text-center w-100">
                  N/A
                  <i className={'mx-2 bi bi-info-circle traffic-light-red'} />
                </p>
              </OverlayTrigger>}
          </td>
          <td>{po.siteName}</td>
          <td>
            <div className="dropdown">
              <Button className='dropdown-toggle btn-ghost-secondary' variant='link' data-bs-toggle='dropdown' >
                <strong>Actions</strong>
              </Button>
              <div className='dropdown-menu'>
                {
                  !po.hasTransactions &&
                  <BoldButton
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => props.setModalState({ state: 'Edit', po })}>
                    Edit
                  </BoldButton>
                }
                {
                  po.hasTransactions &&
                  <BoldButton
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => props.setModalState({ state: 'View', po })}>
                    View
                  </BoldButton>
                }
                {
                  !props.completed &&
                  <BoldButton
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setPoToComplete(po)}>
                    Complete
                  </BoldButton>
                }
                <BoldButton
                  className='dropdown-item btn-ghost-secondary'
                  onClick={() => setPoToRemove(po)}>
                  Remove
                </BoldButton>
                {po.hasTransactions &&
                  <BoldButton
                    className="dropdown-item btn-ghost-secondary"
                    onClick={() => props.setViewTransaction(po)}>
                    View Transactions
                  </BoldButton>}
              </div>
            </div>
          </td>
        </tr>
      )}
    </tbody>
  </Table >;
};

export default PurchaseOrdersTab;
